
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Clock, CheckCircle, XCircle, Plus, Calendar, MapPin, FileText } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

const MySubmissions = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: submissions, isLoading } = useQuery({
    queryKey: ['my-submissions', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('user_submissions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 gap-1">
            <Clock className="w-3 h-3" />
            {isRTL ? "قيد المراجعة" : "Pending"}
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1">
            <CheckCircle className="w-3 h-3" />
            {isRTL ? "تمت الموافقة" : "Approved"}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1">
            <XCircle className="w-3 h-3" />
            {isRTL ? "مرفوض" : "Rejected"}
          </Badge>
        );
      default:
        return null;
    }
  };

  const getSubmissionTypeLabel = (type: string) => {
    switch (type) {
      case 'event':
        return isRTL ? "فعالية" : "Event";
      case 'x_space':
        return isRTL ? "X Space" : "X Space";
      case 'social_account':
        return isRTL ? "حساب اجتماعي" : "Social Account";
      default:
        return type;
    }
  };

  if (!user) {
    return (
      <div className={`min-h-screen bg-gray-50 ${isRTL ? "rtl" : "ltr"}`}>
        <div className="container py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold mb-4">
              {isRTL ? "يرجى تسجيل الدخول أولاً" : "Please login first"}
            </h2>
            <Button onClick={() => navigate("/auth")}>
              {isRTL ? "تسجيل الدخول" : "Login"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${isRTL ? "rtl" : "ltr"}`}>
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="gap-2"
            >
              {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
              {isRTL ? "العودة للرئيسية" : "Back to Home"}
            </Button>
            <Button
              onClick={() => navigate("/submit")}
              className="gap-2 bg-gradient-to-r from-primary to-purple-600"
            >
              <Plus className="w-4 h-4" />
              {isRTL ? "إضافة طلب جديد" : "New Submission"}
            </Button>
          </div>
        </div>
      </div>

      <div className="container py-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-6">
          {isRTL ? "طلباتي" : "My Submissions"}
        </h1>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : submissions && submissions.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {submissions.map((submission) => (
              <Card key={submission.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg line-clamp-2">
                      {isRTL && submission.title_ar ? submission.title_ar : submission.title}
                    </CardTitle>
                    {getStatusBadge(submission.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FileText className="w-4 h-4" />
                    <span>{getSubmissionTypeLabel(submission.submission_type)}</span>
                  </div>
                  
                  {submission.date && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{submission.date}</span>
                    </div>
                  )}
                  
                  {(submission.location || submission.location_ar) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span className="line-clamp-1">
                        {isRTL && submission.location_ar ? submission.location_ar : submission.location}
                      </span>
                    </div>
                  )}

                  <div className="pt-2 border-t text-xs text-muted-foreground">
                    {isRTL ? "تاريخ التقديم: " : "Submitted: "}
                    {format(new Date(submission.created_at), "dd MMM yyyy", {
                      locale: isRTL ? ar : undefined,
                    })}
                  </div>

                  {submission.status === 'rejected' && submission.admin_notes && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-red-600">
                        <span className="font-medium">{isRTL ? "سبب الرفض: " : "Reason: "}</span>
                        {submission.admin_notes}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isRTL ? "لا توجد طلبات" : "No submissions yet"}
            </h3>
            <p className="text-gray-500 mb-4">
              {isRTL ? "ابدأ بإضافة فعاليتك الأولى" : "Start by adding your first event"}
            </p>
            <Button
              onClick={() => navigate("/packages")}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              {isRTL ? "أضف فعاليتك" : "Add Your Event"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubmissions;
